import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { approveJobCollaboration } from "@service/auth";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@util/useQuery";
import BasicLayout from "@container/layout/basic";
import { ArrowRightIcon, CheckCircleIcon } from "lucide-react";
import { Checkbox } from "@component/checkbox";

export default function EmployerApproveCollaboration() {
    const queryParams = useQuery()
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isApproved, setIsApproved] = useState<boolean>(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const token = queryParams.get("token") || "";
    const payloadToken = token ? JSON.parse(atob(token)) : null;


    const handleAcceptInvitation = () => {
        setIsLoading(true);
        if (payloadToken.is_register) {
            sessionStorage.setItem('__INVITE_MEMBER_EMAIL__', payloadToken["email"] ?? "")
            navigate('/employer-signup')
        } else {
            approveJobCollaboration(
                payloadToken["email"] ?? "",
                payloadToken["job_id"] ?? ""
            ).then((resp) => {
                if (resp.status === 200) {
                    setIsApproved(true)
                }
            }).catch((err) => {
                if (err.response && err.response.data && err.response.data.code === 404) {
                    setIsApproved(true)
                }
            }).finally(() => {
                setIsLoading(false)
            })
        }
    }

    return (
        <BasicLayout disableMinHeight showFooter={false}>
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 h-[calc(100vh-57px)]">
                {!isApproved ? (
                    <Card className="w-full max-w-xl">
                        <CardContent
                            className="p-6 space-y-6"
                            style={{
                                paddingTop: "32px",
                                paddingRight: "32px",
                                paddingBottom: "32px",
                                paddingLeft: "32px",
                            }}
                        >
                            {/* Header */}
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    You’ve been invited to collaborate on a job listing on{" "}
                                    <span className="text-blue-600 text-2xl font-bold">{payloadToken?.company_is_byoc ? payloadToken?.company_name : "ourteam"}</span>
                                </h2>
                                <p className="text-sm text-gray-600 font-medium">
                                    Please register as <span className="font-bold">{payloadToken?.email}</span> to
                                    accept this invitation.
                                </p>
                            </div>

                            {/* Job Info */}
                            <div>
                                <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/30">
                                    {/* Decorative background elements */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/40 rounded-full blur-3xl" />

                                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />
                                    <div className="relative p-6">
                                        <div className="flex flex-col items-center space-y-4 text-center">
                                            {/* Company Logo with enhanced styling */}
                                            <div className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden flex justify-center items-center bg-white ring-1 ring-blue-200 dark:ring-offset-gray-900">
                                                <img
                                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${payloadToken?.company_is_byoc ? payloadToken?.company_logo : "commons/ourteam_logo_only.png"}`}
                                                    alt={`${payloadToken?.job_title} icon`}
                                                    onError={(e) => {
                                                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                                    }}
                                                    className="relative w-16 object-cover "
                                                />
                                            </div>


                                            {/* Job Details */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-lg font-bold text-gray-900 mb-1">
                                                    {payloadToken?.job_title}
                                                </h3>
                                                <p className="text-sm text-gray-600 font-medium mb-2">
                                                    {payloadToken?.company_is_byoc ? payloadToken?.company_name : "ourteam"}
                                                </p>
                                                <div className="flex items-center justify-center">
                                                    <p className="text-sm text-gray-600">
                                                        Invited by{" "}
                                                        <span className="font-semibold text-gray-900">
                                                            {payloadToken?.inviter_name}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Terms of Use Checkbox */}
                            <div className="flex items-start gap-3 w-full">
                                <Checkbox
                                    id="terms"
                                    checked={termsAccepted}
                                    onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                                    className="mt-0.5"
                                />
                                <label
                                    htmlFor="terms"
                                    className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer leading-relaxed"
                                >
                                    By proceeding, you agree to the{" "}
                                    <a
                                        href="https://ourteam.ai/terms-of-service"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-blue-600 dark:text-blue-400 hover:text-black dark:hover:text-white"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    at ourteam.
                                </label>
                            </div>

                            {/* Accept Invitation Button */}
                            <div className="text-center">
                                <Button
                                    onClick={handleAcceptInvitation}
                                    disabled={isLoading || !termsAccepted}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                                    style={{ paddingTop: "16px", paddingBottom: "16px" }}
                                >
                                    {isLoading ? "Accepting..." : "Register to Accept Invitation"}
                                    {!isLoading && <ArrowRightIcon className="ml-2 h-4 w-4" />}
                                </Button>

                                <div className="mt-6 text-center text-sm">
                                    Already have an account?{" "}
                                    <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                        <Link to="/" className="underline">
                                            Login here.
                                        </Link>
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <div className="mb-6">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                </div>
                                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                                    You're now collaborating on this job!
                                </h1>
                                <p className="text-gray-600">
                                    You've successfully joined as a <strong>Collaborator</strong>
                                    .
                                    <br />
                                    You can now view the job details and its applicants.
                                </p>
                            </div>
                            <Button onClick={() => navigate("/")} className="w-full bg-blue-600 hover:bg-blue-700">
                                Proceed to Login
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </BasicLayout>
    );
}
